import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2, IndianRupee, Link2, Send, RotateCcw, ShoppingCart } from 'lucide-react';
import api from '@/utils/api';
import { toastMessage } from '@/utils/toast-message';

interface ActiveLink { payment_link_id: number; short_url: string; amount: number; expires_at: string | null; sent_at: string | null; }
interface PayableOrder {
  order_uuid: string; order_name: string | null; order_type: 'order' | 'draft' | string;
  financial_status: string | null; placed_at: string | null; products: string | null;
  total: number; paid: number; balance: number; active_link: ActiveLink | null;
}

interface Props {
  leadUuid: string;
  customerName: string;
  onClose: () => void;
  onSent: () => void;
}

const inr = (v: number) => `₹${(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmt = (s: string | null) =>
  s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

/**
 * Send a Razorpay payment link from the WhatsApp Inbox. The agent picks the order and
 * the amount; the CRM creates the link (POST /razorpay/orders/{uuid}/payment-link) and
 * sends it on crm_payment_request with the real link behind "Pay Now"
 * (POST /razorpay/payment-links/{id}/send-whatsapp), which also logs it in this thread.
 * The agent never handles a URL, and every link belongs to an order.
 */
const SendPaymentLinkModal: React.FC<Props> = ({ leadUuid, customerName, onClose, onSent }) => {
  const [orders, setOrders] = useState<PayableOrder[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.req({ url: `/razorpay/leads/${leadUuid}/payable-orders`, method: api.Method.GET })
      .then((res) => {
        const list = ((res as { data?: PayableOrder[] })?.data) || [];
        setOrders(list);
        if (list[0]) { setSelected(list[0].order_uuid); setAmount(String(list[0].balance || list[0].total)); }
      })
      .catch(() => { toastMessage('Could not load this customer\'s orders', 'error'); setOrders([]); });
  }, [leadUuid]);

  const order = useMemo(() => orders?.find((o) => o.order_uuid === selected) || null, [orders, selected]);

  const pick = (o: PayableOrder) => { setSelected(o.order_uuid); setAmount(String(o.balance || o.total)); };

  const send = async (paymentLinkId: number) => {
    const res = await api.req({ url: `/razorpay/payment-links/${paymentLinkId}/send-whatsapp`, method: api.Method.POST, data: {} }) as
      { success?: boolean; message?: string } | null;
    if (res && res.success === false) throw new Error(res.message || 'WhatsApp send failed');
  };

  const createAndSend = async () => {
    if (!order) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toastMessage('Enter an amount greater than 0', 'error'); return; }
    if (amt > order.total + 0.01) { toastMessage(`Amount cannot be more than the order total (${inr(order.total)})`, 'error'); return; }
    setBusy(true);
    try {
      const created = await api.req({ url: `/razorpay/orders/${order.order_uuid}/payment-link`, method: api.Method.POST, data: { amount: amt } }) as
        { success?: boolean; message?: string; data?: { payment_link_id?: number } } | null;
      const id = created?.data?.payment_link_id;
      if (!created?.success || !id) throw new Error(created?.message || 'Could not create the payment link');
      await send(id);
      toastMessage(`Payment link for ${inr(amt)} sent on WhatsApp`, 'success');
      onSent();
      onClose();
    } catch (e) {
      toastMessage((e as Error).message || 'Could not send the payment link', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Draft orders: send the order's own Shopify checkout (invoice) link on crm_1. The
  // server looks the link up in Shopify -- the agent never handles the URL.
  const sendCheckout = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const res = await api.req({ url: `/razorpay/orders/${order.order_uuid}/send-invoice-link`, method: api.Method.POST, data: {} }) as
        { success?: boolean; message?: string } | null;
      if (res && res.success === false) throw new Error(res.message || 'Could not send the checkout link');
      toastMessage(`Checkout link for ${order.order_name || 'the draft'} sent on WhatsApp`, 'success');
      onSent();
      onClose();
    } catch (e) {
      toastMessage((e as Error).message || 'Could not send the checkout link', 'error');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!order?.active_link) return;
    setBusy(true);
    try {
      await send(order.active_link.payment_link_id);
      toastMessage(`Existing link for ${inr(order.active_link.amount)} re-sent on WhatsApp`, 'success');
      onSent();
      onClose();
    } catch (e) {
      toastMessage((e as Error).message || 'Could not resend the link', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-[#153d23]" />
            <div>
              <p className="font-semibold text-gray-900 text-sm">Send payment or checkout link</p>
              <p className="text-[11.5px] text-gray-500">to {customerName} on WhatsApp · the CRM creates the link, you never paste one</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {orders === null && <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>}
          {orders && orders.length === 0 && (
            <p className="text-sm text-gray-500 py-8 text-center">
              No unpaid order for this customer in the last 60 days.<br />
              Create the order first (Create Order), then send its link here.
            </p>
          )}
          {orders?.map((o) => (
            <button key={o.order_uuid} onClick={() => pick(o)}
                    className={`w-full text-left rounded-lg border p-3 ${selected === o.order_uuid ? 'border-[#153d23] ring-1 ring-[#153d23] bg-green-50/40' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-sm text-gray-900">{o.order_name || 'Order'}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${o.order_type === 'draft' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                    {o.order_type === 'draft' ? 'Draft' : (o.financial_status === 'partially_paid' ? 'Part-paid' : 'COD / unpaid')}
                  </span>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{fmt(o.placed_at)}</span>
              </div>
              {o.products && <p className="text-xs text-gray-500 mt-1 truncate">{o.products}</p>}
              <p className="text-xs text-gray-600 mt-1">
                Total {inr(o.total)}{o.paid > 0 && <> · paid {inr(o.paid)}</>} · <b>due {inr(o.balance)}</b>
              </p>
              {o.active_link && (
                <p className="text-[11px] text-blue-700 mt-1 flex items-center gap-1">
                  <Link2 className="w-3 h-3" /> Active link for {inr(o.active_link.amount)}
                  {o.active_link.expires_at && <> · expires {fmt(o.active_link.expires_at)}</>}
                  {o.active_link.sent_at ? <> · sent {fmt(o.active_link.sent_at)}</> : <> · not sent yet</>}
                </p>
              )}
            </button>
          ))}
        </div>

        {order && (
          <div className="border-t p-4 space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 w-24">Amount (₹)</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal"
                     className="flex-1 h-9 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#153d23]" />
            </div>
            <p className="text-[11px] text-gray-400">
              <b>Create link &amp; send</b>: a Razorpay request for this amount with a "Pay Now" button.
              {order.order_type === 'draft'
                ? <> <b>Checkout (invoice) link</b>: this draft's Shopify checkout page, where the customer completes the order.</>
                : <> A checkout (invoice) link exists only for draft orders.</>}
              {' '}Either one shows in this chat.
            </p>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {order.order_type === 'draft' && (
                <button onClick={sendCheckout} disabled={busy}
                        title="Send this draft's Shopify checkout page, where the customer completes the order and pays"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-[12.5px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  <ShoppingCart className="w-3.5 h-3.5" /> Send checkout (invoice) link
                </button>
              )}
              {order.active_link && (
                <button onClick={resend} disabled={busy}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-[12.5px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  <RotateCcw className="w-3.5 h-3.5" /> Resend existing ({inr(order.active_link.amount)})
                </button>
              )}
              <button onClick={createAndSend} disabled={busy}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#153d23] text-white text-[12.5px] font-bold hover:opacity-90 disabled:opacity-50">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {order.active_link ? 'Create new link & send' : 'Create link & send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendPaymentLinkModal;
